// src/pages/Transactions.js (refactored to RTK Query)
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  CardTitle,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import FilterForm from "./../components/Forms/FilterForms";
import BarcodeScanner from "components/Barcode/ScannerCode";
import NotificationComponent from "components/Alert/alert";
import AddItemModal from "modals/addTransactionModal";
import { DataTableShell } from "../components/ui/TableDataShell";
import { useTranslation } from "../hooks/useTranslation";
import { getApiErrorMessage } from "../api/errorUtils";
// import SaleModal from "modals/SaleModal"; // (left commented as in your code)

// 👉 RTK Query hooks from the barrel
import {
  useFetchProductsByTypeQuery,
  useFetchTransactionsByDateQuery,
  useFetchTransactionTypesQuery,
  useSellProductsMutation,
} from "../api/api";

const Transactions = () => {
  const t = useTranslation();
  const notificationComponentRef = useRef(new NotificationComponent());

  // Local UI state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editItemData, setEditItemData] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  const [products, setProducts] = useState([]); // full product list (from RTK)
  const [filteredProducts, setFilteredProducts] = useState([]); // filtered / selectable

  const [codeFilter, setCodeFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]); // for SaleModal
  const [showSaleModal, setShowSaleModal] = useState(false);

  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Table columns
  const columns = [
    "id",
    "brand",
    "color",
    "number",
    "specific_type",
    "code",
    "quantity",
    "price",
  ];
  const tableHeaders = [
    t("table.id"),
    t("table.brand"),
    t("table.color"),
    t("table.number"),
    t("table.type"),
    t("table.code"),
    t("table.quantity"),
    t("table.price"),
  ];

  const transactionsColumns = [
    "id",
    "brand_name",
    "color_name",
    "number",
    "product_specific_types",
    "code",
    "product_price",
    "payment_type",
    "bank_name",
    "sale_price",
  ];
  const transactionsTableHeaders = [
    t("table.id"),
    t("table.brand"),
    t("table.color"),
    t("table.number"),
    t("table.type"),
    t("table.code"),
    t("table.price"),
    t("table.paymentType"),
    t("table.bankName"),
    t("table.salePrice"),
  ];

  const transactionsHeadersUI = transactionsTableHeaders;

  const {
    data: productsByType = [],
    isFetching: isFetchingProducts,
    isError: isProductsError,
    error: productsError,
  } = useFetchProductsByTypeQuery("sales");

  const {
    data: transactions = [],
    isFetching: isFetchingTx,
    isError: isTxError,
    error: txError,
  } = useFetchTransactionsByDateQuery(startDate);

  const { data: transactionsType = [], isFetching: isFetchingTypes } =
    useFetchTransactionTypesQuery();

  const [sellProductsMutation, { isLoading: isSelling }] =
    useSellProductsMutation();

  // Initialize local products list (only once or when RTK data changes first time)
  useEffect(() => {
    if (products.length === 0 && Array.isArray(productsByType)) {
      setProducts(productsByType);
    }
  }, [productsByType, products.length]);

  // Handle date changes
  const handleStartDateChange = (event) => {
    const val = event.target.value;
    setStartDate(val);
    setSelectedDate(val);
  };

  const resetFilteredProducts = () => {
    setBrandFilter("");
    setCodeFilter("");
    setFilteredProducts([]);
  };

  const removeSalesProduct = (saleData) => {
    // saleData is array of products sold
    saleData.forEach((saleProduct) => {
      setFilteredProducts((prev) =>
        prev.filter((product) => product.id !== saleProduct.id)
      );
      setProducts((prev) =>
        prev.filter((product) => product.id !== saleProduct.id)
      );
    });
  };

  const handleProductSelection = (productId) => {
    const updated = filteredProducts.map((product) =>
      product.id === productId
        ? { ...product, selected: !product.selected }
        : product
    );
    setFilteredProducts(updated);
  };

  const handleCodeFilterChange = (event) => setCodeFilter(event.target.value);
  const handleBrandFilterChange = (event) => setBrandFilter(event.target.value);

  const handleBarcodeData = (filtered) => {
    const existingIds = filteredProducts.map((item) => item.id);
    const newItems = filtered.filter((item) => !existingIds.includes(item.id));
    setFilteredProducts((prev) => [...prev, ...newItems]);
  };

  const handleBarcodeScanned = (scannedValue) => {
    const syntheticEvent = {
      preventDefault: () => {},
      target: { value: scannedValue, barcode: scannedValue },
    };
    handleSubmit(syntheticEvent);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const scannedCode = event?.target?.barcode;

    const filtered = products.filter((product) => {
      const codeCond =
        !codeFilter ||
        String(product.code || "")
          .toLowerCase()
          .includes(codeFilter.toLowerCase());
      const brandCond =
        !brandFilter ||
        String(product.brand || "")
          .toLowerCase()
          .includes(brandFilter.toLowerCase());
      if (scannedCode) {
        const barcodeCondition = scannedCode === product.barcode;
        return barcodeCondition;
      }
      return codeCond && brandCond;
    });

    if (!scannedCode) {
      setFilteredProducts(filtered);
    } else {
      handleBarcodeData(filtered);
    }
  };

  const toggleModal = () => {
    setEditItemData({});
    setModalOpen((v) => !v);
  };

  const handleAllProductSelection = () => {
    const allSelected = filteredProducts.every((p) => p.selected);
    const updated = filteredProducts.map((p) => ({
      ...p,
      selected: !allSelected,
    }));
    setFilteredProducts(updated);
  };

  const handleSale = () => {
    const selected = filteredProducts.filter((p) => p.selected);
    setSelectedProducts(selected);
    setShowSaleModal(true);
  };

  const addNewItem = (formData) => {
    // Modal handles closing and notifications, so we don't need to do anything here
    // The transaction list will automatically refresh via RTK Query cache invalidation
  };

  // ✅ Use RTK mutation for selling products
  const sellProducts = async (saleData) => {
    try {
      await sellProductsMutation(saleData).unwrap();
      setShowSaleModal(false);
      removeSalesProduct(saleData);
      notificationComponentRef.current.showNotification(
        `${
          saleData?.length > 1 ? "Products are" : "Product is"
        } register to sales successfully`,
        "success"
      );
    } catch (e) {
      console.error("Error selling products:", e);
      notificationComponentRef.current.showNotification(
        getApiErrorMessage(e, "Error selling products", {
          404: "One or more selected products are no longer available in your company inventory.",
          409: "One or more selected products cannot be sold in their current state.",
        }),
        "danger"
      );
    }
  };

  const totalSale = (transactions || []).reduce(
    (total, sale) => Number(total) + Number(sale.sale_price || 0),
    0
  );

  return (
    <>
      <div className="content">
        <BarcodeScanner onBarcodeScanned={handleBarcodeScanned} />
        <NotificationComponent ref={notificationComponentRef} />

        {/* Products + Filters (kept commented as in your code) */}
        {/* <Card>
          <CardHeader>
            <Row>
              <Col className="text-left" sm="6">
                <h5 className="card-category">CMS</h5>
                <CardTitle tag="h2">Products</CardTitle>
              </Col>
              <Col sm="12">
                <FilterForm
                  codeFilter={codeFilter}
                  brandFilter={brandFilter}
                  handleCodeFilterChange={handleCodeFilterChange}
                  handleBrandFilterChange={handleBrandFilterChange}
                  handleSubmit={handleSubmit}
                  handleSale={handleSale}
                  resetFilteredProducts={resetFilteredProducts}
                />
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Table className="tablesorter productsTable" responsive>
              <thead className="text-primary">
                <tr>
                  {tableHeaders.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                  <th>
                    <input
                      type="checkbox"
                      checked={filteredProducts.length > 0 && filteredProducts.every((p) => p.selected)}
                      onChange={handleAllProductSelection}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {isFetchingProducts ? (
                  <tr><td colSpan={tableHeaders.length + 1}>{t("common.loading")}</td></tr>
                ) : isProductsError ? (
                  <tr><td colSpan={tableHeaders.length + 1}>{t("products.failedToLoad")}</td></tr>
                ) : (
                  filteredProducts.map((product, index) => (
                    <tr key={index}>
                      {columns.map((key, idx) => (
                        <td className={key} key={idx}>{product[key]}</td>
                      ))}
                      <td>
                        <input
                          type="checkbox"
                          checked={!!filteredProducts.find((p) => p.id === product.id)?.selected}
                          onChange={() => handleProductSelection(product.id)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </CardBody>
        </Card> */}

        {/* Transactions table */}
        <Card>
          <CardHeader>
            <Row>
              <Col className="text-left" sm="3">
                <h5 className="card-category">{t("transactions.title")}</h5>
                <CardTitle tag="h2">{t("transactions.products")}</CardTitle>
              </Col>
              <Col sm="9">
                <Button
                  color="primary"
                  onClick={() => toggleModal()}
                  className="float-right"
                >
                  {t("transactions.addTransaction")}
                </Button>
              </Col>
              <Col sm="12" className="text-center">
                <Col sm="6">
                  <FormGroup>
                    <Label htmlFor="startDate">{t("transactions.selectDate")}</Label>
                    <Input
                      type="date"
                      id="startDate"
                      value={startDate}
                      onChange={handleStartDateChange}
                    />
                  </FormGroup>
                </Col>
              </Col>
            </Row>
          </CardHeader>

          <CardBody>
            {isTxError ? (
              <DataTableShell
                headers={transactionsHeadersUI}
                rows={[]} // no rows means it shows the EmptyState
                isLoading={false}
                emptyProps={{
                  title: t("transactions.failedToLoad"),
                  hint:
                    txError?.data?.message ||
                    t("messages.pleaseTryAgain"),
                  icon: "tim-icons icon-alert-circle-exc",
                  ctaLabel: t("messages.retry"),
                  onCta: () => window.location.reload(), // or wire a refetch if you expose it
                }}
              />
            ) : (
              <>
                <DataTableShell
                  headers={transactionsHeadersUI}
                  rows={transactions || []}
                  isLoading={isFetchingTx}
                  emptyProps={{
                    title: t("transactions.noTransactions"),
                    hint: t("transactions.tryDifferentDate"),
                    icon: "tim-icons icon-components",
                    ctaLabel: t("transactions.addTransaction"),
                    onCta: () => toggleModal(),
                  }}
                  rowRenderer={(sale, index) => (
                    <tr key={index}>
                      {transactionsColumns.map((key, idx) => (
                        <td className={key} key={idx}>
                          {String(sale[key] ?? "—")}
                        </td>
                      ))}
                    </tr>
                  )}
                />

                {/* Total (rendered below the table) */}
                <div className="d-flex justify-content-end mt-2">
                  <div className="text-success">
                    <strong>{t("transactions.total") || "Total"}: {totalSale.toFixed(2)}</strong>
                  </div>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        <AddItemModal
          modalOpen={modalOpen}
          toggleModal={toggleModal}
          transactionsType={transactionsType}
          addNewItem={addNewItem}
          setTableData={() => {}}
          tableData={transactions}
          editItemData={editItemData}
          setEditItemData={setEditItemData}
          notificationComponentRef={notificationComponentRef}
        />

        {/* {showSaleModal && (
          <SaleModal
            selectedProducts={selectedProducts}
            toggle={() => setShowSaleModal(!showSaleModal)}
            sellProducts={sellProducts}
            setSales={() => {}}
          />
        )} */}
      </div>
    </>
  );
};

export default Transactions;
